import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCheckCircle,
  faXmark,
  faLock,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { calculateScoreForSlot } from "../../utils/events/FighterScores";

/**
 * LineupSlot - Displays a slot for fighter selection in the lineup.
 * Features a large slot number badge on the left, fighter info in the middle,
 * and status/actions on the right.
 *
 * @param {Object} props
 * @param {number} props.position - The position in lineup (0-based index)
 * @param {Object} props.fighter - The selected fighter object (or null if empty)
 * @param {function} props.onClick - Callback when slot is clicked to add fighter
 * @param {boolean} props.isLotn - Whether this fighter is the Lock of the Night
 * @param {boolean} props.isAlternateLotn - Whether this fighter is the Alternate Lock of the Night
 * @param {function} props.onToggleLotn - Callback to toggle LOTN status
 * @param {function} props.onToggleAlternateLotn - Callback to toggle Alt LOTN status
 * @param {function} props.onRemove - Callback to remove fighter from slot
 * @param {Object} [props.opponent] - Optional opponent data
 * @param {boolean} props.hasExistingLotn - Whether another fighter is already set as LOTN
 * @param {Function|null} props.onMoveUp - Callback to move fighter up one position (null if at top)
 * @param {Function|null} props.onMoveDown - Callback to move fighter down one position (null if at bottom)
 */
const LineupSlot = ({
  position,
  fighter,
  onClick,
  isLotn,
  isAlternateLotn,
  onToggleLotn,
  onToggleAlternateLotn,
  onRemove,
  opponent = null,
  hasExistingLotn = false,
  onMoveUp = null,
  onMoveDown = null,
}) => {
  // Format fighter name as "F. LastName"
  const formatFighterName = (f) => {
    if (!f) return "EMPTY";
    return `${f.FirstName.charAt(0)}. ${f.LastName}`;
  };

  // Format opponent name
  const formatOpponentName = (opp) => {
    if (!opp) return "Opponent";
    return `${opp.FirstName.charAt(0)}. ${opp.LastName}`;
  };

  // Handle the lock button click
  const handleLockClick = (e) => {
    e.stopPropagation();
    if (isLotn) {
      onToggleLotn(fighter.FighterId);
    } else if (isAlternateLotn) {
      onToggleAlternateLotn(fighter);
    } else {
      if (hasExistingLotn) {
        onToggleAlternateLotn(fighter);
      } else {
        onToggleLotn(fighter.FighterId);
      }
    }
  };

  // Lock button style
  const getLockButtonStyle = () => {
    if (isLotn) return "bg-gold text-white";
    if (isAlternateLotn) return "bg-blue-500 text-white";
    return "bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-dark-grey";
  };

  // Lock button title
  const getLockButtonTitle = () => {
    if (isLotn) return "Lock of the Night - Click to remove";
    if (isAlternateLotn) return "Alternate Lock of the Night - Click to remove";
    if (hasExistingLotn) return "Click to set as Alternate Lock of the Night";
    return "Click to set as Lock of the Night";
  };

  const isLockButtonDisabled =
    !isLotn && !isAlternateLotn && !hasExistingLotn && isAlternateLotn;

  // --- EMPTY SLOT ---
  if (!fighter) {
    return (
      <button
        onClick={() => onClick(position)}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border-2 border-gray-200 bg-white hover:border-deep-red/40 hover:shadow-sm transition-all duration-200 group"
        aria-label={`Slot ${position + 1} - empty, click to select a fighter`}
      >
        {/* Slot number badge */}
        <div className="w-11 h-11 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-lg font-bold text-medium-grey flex-shrink-0 group-hover:border-deep-red/30 group-hover:text-deep-red transition-colors">
          {position + 1}
        </div>

        {/* Slot info */}
        <div className="flex-1 text-left min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-medium text-medium-grey">
            Slot {position + 1}
          </div>
          <div className="text-sm font-bold text-gray-400">EMPTY</div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-medium-grey group-hover:text-deep-red transition-colors">
            Select a fighter
          </span>
          <FontAwesomeIcon
            icon={faChevronRight}
            className="text-xs text-gray-400 group-hover:text-deep-red transition-colors"
          />
        </div>
      </button>
    );
  }

  // --- FILLED SLOT ---
  return (
    <div
      className={`w-full rounded-xl border-2 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md ${
        isLotn
          ? "border-gold bg-gradient-to-r from-gold/20 via-gold/10 to-gold/20 hover:border-gold ring-2 ring-gold/30"
          : isAlternateLotn
            ? "border-blue-400 bg-gradient-to-r from-blue-50/50 via-blue-50/30 to-blue-50/50 hover:border-blue-500 ring-2 ring-blue-200/50"
            : "border-gold/60 bg-white hover:border-gold"
      }`}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-3 py-3">
        {/* Slot number badge */}
        <div
          className={`w-11 h-11 rounded-lg flex items-center justify-center text-lg font-bold flex-shrink-0 ${
            isLotn
              ? "bg-gold text-white border border-gold shadow-md"
              : isAlternateLotn
                ? "bg-blue-500 text-white border border-blue-500 shadow-md"
                : "bg-deep-red/5 border border-deep-red/20 text-deep-red"
          }`}
        >
          {position + 1}
        </div>

        {/* Fighter info */}
        <div className="flex-1 min-w-0">
          <div
            className={`text-[10px] uppercase tracking-wider font-medium ${
              isLotn ? "text-gold font-bold" : "text-gold"
            }`}
          >
            {isLotn ? "Lock of the Night" : `Slot ${position + 1}`}
          </div>
          <div className="text-sm font-bold text-dark-grey truncate">
            {formatFighterName(fighter)}
          </div>
          <div
            className={`text-xs ${isLotn ? "text-dark-grey/70" : "text-medium-grey"}`}
          >
            vs {formatOpponentName(opponent)}
          </div>
        </div>

        {/* Right side: status + open fighter selection */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className={`text-sm ${isLotn ? "text-gold" : "text-green-500"}`}
          />
          <span
            className={`text-xs font-bold uppercase tracking-wide hidden sm:inline ${
              isLotn ? "text-gold" : "text-green-600"
            }`}
          >
            {isLotn ? "LOTN" : "Positioned"}
          </span>
          <button
            onClick={() => onClick(position)}
            className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
              isLotn ? "hover:bg-gold/20" : "hover:bg-gray-100"
            }`}
            aria-label="Change fighter for this slot"
            title="Change fighter"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-xs text-gold"
            />
          </button>
        </div>
      </div>

      {/* Action bar */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-t ${
          isLotn ? "bg-gold/10 border-gold/30" : "bg-gray-50 border-gray-100"
        }`}
      >
        {/* Left: points + LOTN badge */}
        <div className="flex items-center gap-2">
          <div
            className={`text-sm font-bold ${isLotn ? "text-gold" : "text-deep-red"}`}
          >
            {calculateScoreForSlot(fighter.Moneyline, position)}
            {isLotn && <span className="text-gold ml-0.5">×2</span>}
            <span
              className={`text-[10px] ml-1 font-medium ${isLotn ? "text-dark-grey/60" : "text-medium-grey"}`}
            >
              pts
            </span>
          </div>
          {(isLotn || isAlternateLotn) && (
            <span
              className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                isLotn
                  ? "text-white bg-gold border border-gold shadow-sm"
                  : "text-blue-500 bg-blue-50 border border-blue-200"
              }`}
            >
              {isLotn ? "LOTN ×2" : "ALT"}
            </span>
          )}
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-1">
          {/* Move up */}
          <button
            onClick={onMoveUp}
            disabled={!onMoveUp}
            className={`w-7 h-7 flex items-center justify-center rounded transition-all duration-150 ${
              onMoveUp
                ? "text-medium-grey hover:bg-gray-200 hover:text-dark-grey active:scale-95"
                : "text-gray-300 cursor-not-allowed"
            }`}
            aria-label="Move fighter up"
            title="Move up"
          >
            <FontAwesomeIcon icon={faChevronUp} className="text-xs" />
          </button>

          {/* Move down */}
          <button
            onClick={onMoveDown}
            disabled={!onMoveDown}
            className={`w-7 h-7 flex items-center justify-center rounded transition-all duration-150 ${
              onMoveDown
                ? "text-medium-grey hover:bg-gray-200 hover:text-dark-grey active:scale-95"
                : "text-gray-300 cursor-not-allowed"
            }`}
            aria-label="Move fighter down"
            title="Move down"
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
          </button>

          {/* Lock button */}
          <button
            onClick={handleLockClick}
            className={`w-7 h-7 flex items-center justify-center rounded transition-all duration-150 ${getLockButtonStyle()} ${
              isLockButtonDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:scale-105 active:scale-95"
            }`}
            aria-label={getLockButtonTitle()}
            title={getLockButtonTitle()}
            disabled={isLockButtonDisabled}
          >
            <FontAwesomeIcon icon={faLock} className="text-xs" />
          </button>

          {/* Remove button */}
          <button
            onClick={() => onRemove(position)}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-500 active:scale-95 transition-all duration-150"
            aria-label="Remove fighter"
            title="Remove fighter"
          >
            <FontAwesomeIcon icon={faXmark} className="text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineupSlot;
